/**
 * PaymentAccountModal Component
 * Modal for creating and editing payment accounts
 * Supports add mode (no account prop) and edit mode (with account prop)
 */

import { useState, useEffect, useRef, Fragment } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import {
  XMarkIcon,
  BanknotesIcon,
  EyeIcon,
  EyeSlashIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import {
  createPaymentAccount,
  updatePaymentAccount,
  lookupRoutingNumber,
} from '../../../services/oathPaymentAccountService';
import { toast } from 'react-toastify';
import { OathSpinner, OathConfirmDialog } from '../../shared/OathUI';

const ACCOUNT_TYPES = [
  { value: 'C', label: 'Checking' },
  { value: 'S', label: 'Savings' },
];

const PAYMENT_TYPES = [
  { value: '', label: 'None' },
  { value: 'NET', label: 'NET' },
  { value: 'BUF', label: 'BUF' },
  { value: 'BOTH', label: 'BOTH' },
];

const PaymentAccountModal = ({ isOpen, onClose, onSuccess, agencyId, account }) => {
  const isEditMode = !!account;
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // Escape key closes directly — capture phase so it runs before Headless UI's handler
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, onClose]);

  const [formData, setFormData] = useState({
    routingNumber: '',
    accountNumber: '',
    nameOnAccount: '',
    nickName: '',
    accountType: 'C',
    defaultPaymentType: '',
    active: true,
  });
  const [showAccount, setShowAccount] = useState(false);
  const [confirmAccountNumber, setConfirmAccountNumber] = useState('');
  const [showConfirmAccount, setShowConfirmAccount] = useState(false);
  const [routingSearch, setRoutingSearch] = useState('');
  const [routingOptions, setRoutingOptions] = useState([]);
  const [routingLoading, setRoutingLoading] = useState(false);
  const [showRoutingDropdown, setShowRoutingDropdown] = useState(false);
  const [selectedBank, setSelectedBank] = useState('');
  const routingDebounceRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [acknowledged, setAcknowledged] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setErrors({});
      setShowAccount(false);
      setShowConfirmAccount(false);
      setConfirmAccountNumber('');
      setAcknowledged(false);
      setRoutingSearch('');
      setRoutingOptions([]);
      setShowRoutingDropdown(false);
      setSelectedBank('');

      if (isEditMode && account) {
        // Pre-fill with masked/available data - routing and account numbers stay blank
        // until user fetches decrypted values or types new ones
        setFormData({
          routingNumber: '',
          accountNumber: '',
          nameOnAccount: account.nameOnAccount || '',
          nickName: account.nickName || '',
          accountType: account.accountType || 'Checking',
          defaultPaymentType: account.defaultPaymentType || '',
          active: account.isActive !== undefined ? account.isActive : true,
        });
      } else {
        setFormData({
          routingNumber: '',
          accountNumber: '',
          nameOnAccount: '',
          nickName: '',
          accountType: 'C',
          defaultPaymentType: '',
          active: true,
          shared: false,
        });
      }
    }
  }, [isOpen, isEditMode, account]);

  const handleInputChange = (name, value) => {
    // Only allow digits for routing and account numbers
    if (name === 'routingNumber') {
      value = value.replace(/\D/g, '').slice(0, 9);
    }
    if (name === 'accountNumber') {
      value = value.replace(/\D/g, '').slice(0, 17);
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
    if (name === 'accountNumber' && errors.confirmAccountNumber) {
      setErrors((prev) => ({ ...prev, confirmAccountNumber: null }));
    }
  };

  // Routing number typeahead search
  const handleRoutingSearch = (value) => {
    value = value.replace(/\D/g, '').slice(0, 9);
    setRoutingSearch(value);
    if (formData.routingNumber) {
      setFormData((prev) => ({ ...prev, routingNumber: '' }));
      setSelectedBank('');
    }
    if (errors.routingNumber) {
      setErrors((prev) => ({ ...prev, routingNumber: null }));
    }

    if (routingDebounceRef.current) clearTimeout(routingDebounceRef.current);

    if (value.length < 4) {
      setRoutingOptions([]);
      setShowRoutingDropdown(false);
      return;
    }

    setRoutingLoading(true);
    routingDebounceRef.current = setTimeout(async () => {
      const result = await lookupRoutingNumber(agencyId, value);
      setRoutingLoading(false);
      if (result.success) {
        setRoutingOptions(result.results);
        setShowRoutingDropdown(result.results.length > 0);
      } else {
        setRoutingOptions([]);
        setShowRoutingDropdown(false);
      }
    }, 300);
  };

  const handleRoutingSelect = (option) => {
    setFormData((prev) => ({ ...prev, routingNumber: option.routingNumber }));
    setRoutingSearch(option.routingNumber);
    setSelectedBank(option.bankName);
    setShowRoutingDropdown(false);
    setRoutingOptions([]);
    if (errors.routingNumber) {
      setErrors((prev) => ({ ...prev, routingNumber: null }));
    }
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (routingDebounceRef.current) clearTimeout(routingDebounceRef.current);
    };
  }, []);

  const validate = () => {
    const newErrors = {};

    if (!isEditMode || routingSearch) {
      if (!formData.routingNumber) {
        newErrors.routingNumber = routingSearch
          ? 'Please select a routing number from the results'
          : 'Routing number is required';
      }
    }

    if (!isEditMode || formData.accountNumber) {
      if (!formData.accountNumber) {
        newErrors.accountNumber = 'Account number is required';
      } else if (formData.accountNumber.length < 6 || formData.accountNumber.length > 17) {
        newErrors.accountNumber = 'Account number must be 6-17 digits';
      } else if (confirmAccountNumber !== formData.accountNumber) {
        newErrors.confirmAccountNumber = 'Account numbers do not match';
      }
    }

    if (!formData.nameOnAccount.trim()) {
      newErrors.nameOnAccount = 'Name on account is required';
    }

    if (!formData.nickName?.trim()) {
      newErrors.nickName = 'Nickname is required';
    }

    if (!formData.accountType) {
      newErrors.accountType = 'Account type is required';
    }

    if (!isEditMode && !acknowledged) {
      newErrors.acknowledged = 'You must certify that you are an authorized user on this account';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);

    let result;
    if (isEditMode) {
      // Only send fields that have values (routing/account optional on edit)
      const updateData = {
        nameOnAccount: formData.nameOnAccount.trim(),
        nickName: formData.nickName.trim(),
        active: formData.active,
        defaultPaymentType: formData.defaultPaymentType || undefined,
      };
      if (formData.routingNumber) updateData.routingNumber = formData.routingNumber;
      if (formData.accountNumber) updateData.accountNumber = formData.accountNumber;

      result = await updatePaymentAccount(agencyId, account.accountId, updateData);
    } else {
      result = await createPaymentAccount(agencyId, {
        routingNumber: formData.routingNumber,
        accountNumber: formData.accountNumber,
        nameOnAccount: formData.nameOnAccount.trim(),
        nickName: formData.nickName.trim(),
        accountType: formData.accountType,
        defaultPaymentType: formData.defaultPaymentType || undefined,
        shared: formData.shared,
      });
    }

    if (result.success) {
      toast.success(isEditMode ? 'Payment account updated' : 'Payment account created');
      onSuccess?.();
    } else {
      toast.error(result.error || `Failed to ${isEditMode ? 'update' : 'create'} payment account`);
    }

    setIsSubmitting(false);
  };

  return (
    <>
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => setShowCloseConfirm(true)}>
        {/* Backdrop */}
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </TransitionChild>

        {/* Modal */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-2xl bg-white dark:bg-gray-800 rounded-xl shadow-xl overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-primary-50/50 dark:bg-gray-700/50">
                  <div className="flex items-center gap-2">
                    <BanknotesIcon className="h-5 w-5 text-primary dark:text-primary-light" />
                    <DialogTitle className="font-semibold text-gray-900 dark:text-white">
                      {isEditMode ? 'Edit Payment Account' : 'Add Payment Account'}
                    </DialogTitle>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} autoComplete="new-password">
                  <div className="p-4 space-y-3">
                    {/* Row 1: Nickname + Name on Account */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Nickname <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.nickName}
                          onChange={(e) => handleInputChange('nickName', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-md text-sm bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary ${
                            errors.nickName ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
                          }`}
                          placeholder="Primary Checking"
                          autoComplete="new-password"
                          data-1p-ignore
                          data-lpignore="true"
                        />
                        {errors.nickName && (
                          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.nickName}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Name on Account <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={formData.nameOnAccount}
                          onChange={(e) => handleInputChange('nameOnAccount', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-md text-sm bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary ${
                            errors.nameOnAccount ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
                          }`}
                          placeholder="ABC Bail Bonds"
                          autoComplete="new-password"
                          data-1p-ignore
                          data-lpignore="true"
                        />
                        {errors.nameOnAccount && (
                          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.nameOnAccount}</p>
                        )}
                      </div>
                    </div>

                    {/* Row 2: Routing + Account side by side */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Routing Number - Typeahead Lookup */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Routing Number {!isEditMode && <span className="text-red-500">*</span>}
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={routingSearch}
                          onChange={(e) => handleRoutingSearch(e.target.value)}
                          onFocus={() => {
                            if (routingOptions.length > 0 && !formData.routingNumber) {
                              setShowRoutingDropdown(true);
                            }
                          }}
                          onBlur={() => setTimeout(() => setShowRoutingDropdown(false), 150)}
                          className={`w-full px-3 py-2 pr-10 border rounded-md text-sm font-mono bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary ${
                            errors.routingNumber ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
                          }`}
                          placeholder="Search routing number..."
                          maxLength={9}
                          autoComplete="new-password"
                          data-1p-ignore
                          data-lpignore="true"
                        />
                        {routingLoading && (
                          <div className="absolute right-2 top-1/2 -translate-y-1/2">
                            <ArrowPathIcon className="h-4 w-4 text-gray-400 animate-spin" />
                          </div>
                        )}

                        {/* Routing lookup dropdown */}
                        {showRoutingDropdown && (
                          <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-48 overflow-y-auto">
                            {routingOptions.map((option) => (
                              <button
                                key={option.routingNumber}
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => handleRoutingSelect(option)}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-primary/10 dark:hover:bg-primary/20 transition-colors flex justify-between items-center gap-2"
                              >
                                <span className="font-mono text-gray-900 dark:text-white">{option.routingNumber}</span>
                                <span className="text-gray-500 dark:text-gray-400 text-xs truncate">{option.bankName}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {errors.routingNumber ? (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.routingNumber}</p>
                      ) : !selectedBank && !formData.routingNumber && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Type at least 4 digits to search</p>
                      )}
                      {selectedBank && (
                        <p className="mt-1 text-xs text-primary dark:text-primary-light font-medium">{selectedBank}</p>
                      )}
                      {isEditMode && account?.maskedRoutingNumber && !selectedBank && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Current: {account.maskedRoutingNumber}
                        </p>
                      )}
                    </div>

                    {/* Account Number */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Account Number {!isEditMode && <span className="text-red-500">*</span>}
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={formData.accountNumber}
                          onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                          className={`w-full px-3 py-2 pr-10 border rounded-md text-sm font-mono bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary ${
                            errors.accountNumber ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
                          }`}
                          style={showAccount ? undefined : { WebkitTextSecurity: 'disc' }}
                          placeholder={isEditMode ? 'Leave blank to keep current' : '6-17 digit account number'}
                          maxLength={17}
                          autoComplete="new-password"
                          data-1p-ignore
                          data-lpignore="true"
                        />
                        <button
                          type="button"
                          onClick={() => setShowAccount(!showAccount)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          {showAccount ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                        </button>
                      </div>
                      {errors.accountNumber && (
                        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.accountNumber}</p>
                      )}
                      {isEditMode && account?.maskedAccountNumber && !formData.accountNumber && (
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Current: {account.maskedAccountNumber}
                        </p>
                      )}
                    </div>
                    </div>

                    {/* Confirm Account Number - shown when account number has been entered */}
                    {formData.accountNumber && (
                      <div className="sm:grid sm:grid-cols-2 sm:gap-3">
                        <div className="sm:col-start-2">
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Confirm Account Number <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              value={confirmAccountNumber}
                              onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, '').slice(0, 17);
                                setConfirmAccountNumber(val);
                                if (errors.confirmAccountNumber) {
                                  setErrors((prev) => ({ ...prev, confirmAccountNumber: null }));
                                }
                              }}
                              className={`w-full px-3 py-2 pr-10 border rounded-md text-sm font-mono bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary ${
                                errors.confirmAccountNumber ? 'border-red-300 dark:border-red-500' :
                                confirmAccountNumber && confirmAccountNumber === formData.accountNumber ? 'border-green-300 dark:border-green-500' :
                                'border-gray-300 dark:border-gray-600'
                              }`}
                              style={showConfirmAccount ? undefined : { WebkitTextSecurity: 'disc' }}
                              placeholder="Re-enter account number"
                              maxLength={17}
                              autoComplete="new-password"
                              data-1p-ignore
                              data-lpignore="true"
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmAccount(!showConfirmAccount)}
                              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                              {showConfirmAccount ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                            </button>
                          </div>
                          {errors.confirmAccountNumber ? (
                            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.confirmAccountNumber}</p>
                          ) : confirmAccountNumber && confirmAccountNumber === formData.accountNumber ? (
                            <p className="mt-1 text-xs text-green-600 dark:text-green-400">Account numbers match</p>
                          ) : confirmAccountNumber && confirmAccountNumber.length >= formData.accountNumber.length ? (
                            <p className="mt-1 text-xs text-red-600 dark:text-red-400">Account numbers do not match</p>
                          ) : null}
                        </div>
                      </div>
                    )}

                    {/* Row 3: Account Type + Payment Type */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Account Type <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.accountType}
                          onChange={(e) => handleInputChange('accountType', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-md text-sm bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary ${
                            errors.accountType ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
                          }`}
                        >
                          {ACCOUNT_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                        {errors.accountType && (
                          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.accountType}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Default Payment Type
                        </label>
                        <select
                          value={formData.defaultPaymentType}
                          onChange={(e) => handleInputChange('defaultPaymentType', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary"
                        >
                          {PAYMENT_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Shared / Personal selection (add mode only) */}
                    {!isEditMode && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Account Visibility
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => handleInputChange('shared', false)}
                            className={`p-3 rounded-lg border-2 text-left transition-all ${
                              !formData.shared
                                ? 'border-primary bg-primary/5 dark:bg-primary/10'
                                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                            }`}
                          >
                            <span className={`block text-sm font-semibold ${!formData.shared ? 'text-primary dark:text-primary-light' : 'text-gray-700 dark:text-gray-300'}`}>
                              Personal
                            </span>
                            <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              Only visible to you
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleInputChange('shared', true)}
                            className={`p-3 rounded-lg border-2 text-left transition-all ${
                              formData.shared
                                ? 'border-primary bg-primary/5 dark:bg-primary/10'
                                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                            }`}
                          >
                            <span className={`block text-sm font-semibold ${formData.shared ? 'text-primary dark:text-primary-light' : 'text-gray-700 dark:text-gray-300'}`}>
                              Shared
                            </span>
                            <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                              Visible to all authorized users in this agency
                            </span>
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Active toggle (edit mode only) */}
                    {isEditMode && (
                      <div className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Account Active
                        </span>
                        <button
                          type="button"
                          onClick={() => handleInputChange('active', !formData.active)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            formData.active ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                              formData.active ? 'translate-x-6' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    )}

                    {/* Acknowledgement (add mode only) */}
                    {!isEditMode && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Authorization <span className="text-red-500">*</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            setAcknowledged(!acknowledged);
                            if (errors.acknowledged) {
                              setErrors((prev) => ({ ...prev, acknowledged: null }));
                            }
                          }}
                          className={`w-full p-3 rounded-lg border-2 text-left transition-all flex items-center gap-3 ${
                            acknowledged
                              ? 'border-primary bg-primary/5 dark:bg-primary/10'
                              : errors.acknowledged
                                ? 'border-red-300 dark:border-red-500 bg-red-50/30 dark:bg-red-900/10'
                                : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                          }`}
                        >
                          <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                            acknowledged
                              ? 'bg-primary border-primary'
                              : errors.acknowledged
                                ? 'border-red-300 dark:border-red-500'
                                : 'border-gray-300 dark:border-gray-600'
                          }`}>
                            {acknowledged && (
                              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <span className={`text-sm ${acknowledged ? 'font-medium text-primary dark:text-primary-light' : 'text-gray-700 dark:text-gray-300'}`}>
                            I certify that I am an authorized user on this account
                          </span>
                        </button>
                        {errors.acknowledged && (
                          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.acknowledged}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex justify-end gap-3 px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                    <button
                      type="button"
                      onClick={onClose}
                      disabled={isSubmitting}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-md transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isSubmitting ? (
                        <>
                          <OathSpinner size="sm" className="text-white" />
                          {isEditMode ? 'Saving...' : 'Creating...'}
                        </>
                      ) : (
                        isEditMode ? 'Save Changes' : 'Create Account'
                      )}
                    </button>
                  </div>
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>

    <OathConfirmDialog
      open={showCloseConfirm}
      title="Close this window?"
      message="Any unsaved changes will be lost."
      confirmLabel="Close"
      cancelLabel="Go Back"
      variant="warning"
      onConfirm={() => { setShowCloseConfirm(false); onClose(); }}
      onCancel={() => setShowCloseConfirm(false)}
    />
    </>
  );
};

export default PaymentAccountModal;
